import React, { FC, useState } from 'react'
import {
  Box,
  Grid,
  InputLabel,
  InputLabelProps,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import useStyles from './custom-textfield-style'

interface ILabelProps extends InputLabelProps {
  id?: string
  label?: string | React.ReactNode
  error?: boolean
}

export type ITextFieldProps = {
  classNames?: string
  showPassword?: boolean
  touched?: any
  errors?: any
  hasServiceError?: boolean
  values: any
  getFieldMeta: any
  setFieldTouched: any
  setFieldError: any
} & TextFieldProps

const CustomInputLabel: FC<ILabelProps> = ({ id, label }) => (
  <InputLabel {...(id && { htmlFor: id })}>
    <Typography variant="h6" component="span">
      {label}
    </Typography>
  </InputLabel>
)

const moveCursorToEnd = (el: any) => {
  if (typeof el.selectionStart === 'number') {
    setTimeout(() => {
      const length = el.value.length
      el.setSelectionRange(length, length)
    }, 0)
  } else if (typeof el.createTextRange != 'undefined') {
    el.focus()
    let range = el.createTextRange()
    range.collapse(false)
    range.select()
  }
}

const TextFieldTypeText: FC<ITextFieldProps> = ({
  id,
  classNames,
  type,
  label,
  values,
  touched,
  errors,
  hasServiceError,
  showPassword,
  onBlur,
  getFieldMeta,
  setFieldTouched,
  setFieldError,
  ...rest
}) => (
  <>
    {type !== 'password' && <CustomInputLabel id={id} label={label} />}

    <TextField
      fullWidth
      id={id}
      className={classNames}
      type={showPassword ? 'text' : type}
      variant="outlined"
      helperText={id && (touched[id] ? errors[id] : '')}
      error={hasServiceError || (id && touched[id] && Boolean(errors[id]))}
      onBlur={() => {
        const fields = getFieldMeta(id)
        if (!fields.touched && !values[id as string]) return
        setFieldTouched(id, true)
        setFieldError(id, errors[id as string] as string)
      }}
      {...rest}
    />
  </>
)

TextFieldTypeText.defaultProps = {
  type: 'text',
}

const TextFieldTypePassword: FC<ITextFieldProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const { classes } = useStyles()

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword)
    moveCursorToEnd(document.getElementById(props.id as string))
  }

  const handleShowPasswordMouseDown = (e: any) => {
    e.preventDefault()
  }

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <CustomInputLabel id={props.id} label={props.label} />
        </Grid>
        <Grid item className={classes.passwordVisibility}>
          <Box
            onClick={handleShowPasswordClick}
            onMouseDown={handleShowPasswordMouseDown}
          >
            <Typography
              variant="h6"
              color="primary"
              component="span"
              className={classes.label}
            >
              {showPassword ? 'Hide' : 'Show'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <TextFieldTypeText {...props} showPassword={showPassword} />
    </>
  )
}

const CustomTextField: FC<ITextFieldProps> = (props) =>
  props.type === 'password' ? (
    <TextFieldTypePassword {...props} />
  ) : (
    <TextFieldTypeText {...props} />
  )

export default CustomTextField
